import { isAxiosError } from "axios";

import type { AiReputationSource } from "../types/aiReputation";

interface FastApiValidationError {
  loc: (string | number)[];
  msg: string;
}

export const humanizeAiReputationValue = (value: string | null | undefined): string => {
  if (!value) return "Other / Unknown";
  return value.replace(/[_-]/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
};

/**
 * Extracts a human-readable message from a caught API error. Handles FastAPI 422 `detail` arrays,
 * plain `detail` strings, and falls back to the native error message.
 *
 * @example
 *   ```ts
 *   const msg = extractApiErrorMessage(err, "Unable to save.");
 *   // => "Real competitors: List should have at least 1 item after validation, not 0"
 *   ```;
 *
 * @param caught - The value caught in a try/catch block
 * @param fallback - Message used when no structured detail is present
 * @returns A human-readable error string
 */
export const extractApiErrorMessage = (caught: unknown, fallback: string): string => {
  if (!isAxiosError(caught)) {
    return caught instanceof Error ? caught.message : fallback;
  }
  const detail: unknown = caught.response?.data?.detail;
  if (Array.isArray(detail)) {
    return (detail as FastApiValidationError[])
      .map(({ loc, msg }) => {
        const field = loc.at(-1);
        const label =
          typeof field === "string" ? humanizeAiReputationValue(field) : String(field ?? "Field");
        return `${label}: ${msg}`;
      })
      .join("; ");
  }
  if (typeof detail === "string") return detail;
  return caught.message ?? fallback;
};

export const isSafeOutboundUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (
      (url.protocol === "http:" || url.protocol === "https:") && !url.username && !url.password
    );
  } catch {
    return false;
  }
};

export const groupSourcesByDomain = (
  sources: AiReputationSource[],
): Map<string, AiReputationSource[]> => {
  const groups = new Map<string, AiReputationSource[]>();
  [...sources]
    .sort((left, right) => right.influence_score - left.influence_score)
    .forEach((source) => {
      const domain = isSafeOutboundUrl(source.url)
        ? new URL(source.url).hostname
        : "Unknown domain";
      groups.set(domain, [...(groups.get(domain) ?? []), source]);
    });
  return groups;
};
